import { Field, Fieldset } from '@headlessui/react';
import deepEqual from 'deep-equal';
import { FC, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { breadcrumbTitleKeys, useBreadcrumbsContext } from '@/breadcrumbs';
import { Label } from '@/components/form/label';
import { Legend } from '@/components/form/legend';
import { Select } from '@/components/form/select';
import { SelectField } from '@/components/form/select-field';
import { SwitchField } from '@/components/form/switch-field';
import { TextInput } from '@/components/form/text-input';
import { Card } from '@/components/ui/card';
import { CardDivider } from '@/components/ui/card-divider';
import { HeadingGroup } from '@/components/ui/heading-group';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { accessibilitySpeakingRates } from '@/constants/accessibility-speaking-rates';
import { strings } from '@/constants/strings';
import { urls } from '@/constants/urls';
import { useCurrentOrganisation } from '@/hooks/use-current-organisation';
import {
  usePolicies,
  useUpdateAccessibilityPolicySettingsMutation,
  useUpdateWellbeingCheckSettingsMutation,
} from '@/hooks/use-policies';
import { useSelectOptionsFromArray } from '@/hooks/use-select-options';
import { useUserRole } from '@/hooks/use-user-role';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { SectionContainerLayout } from '@/layouts/section-container-layout';
import { AccessibilityPolicySettings } from '@/types/accessibility-policy-settings';
import { WellbeingCheckSettings } from '@/types/wellbeing-check-settings';
import { AddressBookPanel } from './address-book/address-book';

// TODO: add loading ui
/**
 * Development notes:
 *
 * Fields are checked for changes onBlur
 */
export const OrganisationPoliciesPage: FC = () => {
  const { pathname } = useLocation();
  const { setTitles: setBreadcrumbTitles } = useBreadcrumbsContext();
  const { organisation } = useCurrentOrganisation();
  const { isPlatformAdmin } = useUserRole();
  const { data: policies } = usePolicies();
  const [updateWellbeingCheckSettings] =
    useUpdateWellbeingCheckSettingsMutation(policies);
  const [updateAccessibilityPolicySettings] =
    useUpdateAccessibilityPolicySettingsMutation(policies);
  const speakingRateSelectOptions = useSelectOptionsFromArray(
    accessibilitySpeakingRates,
  );

  // TODO: this could all go in a hook which sets up keys based on current unit and current organisation
  useEffect(() => {
    setBreadcrumbTitles((titles) => ({
      ...titles,
      [breadcrumbTitleKeys.currentOrganisationName]: organisation?.name ?? '',
    }));
    return () => {
      setBreadcrumbTitles((titles) => ({
        ...titles,
        [breadcrumbTitleKeys.currentOrganisationName]: '',
      }));
    };
  }, [organisation?.name, setBreadcrumbTitles]);

  const [wellbeingCheckSettings, setWellbeingCheckSettings] = useState<
    WellbeingCheckSettings | undefined
  >(undefined);

  const [accessibilityPolicySettings, setAccessibilityPolicySettings] =
    useState<AccessibilityPolicySettings | undefined>(undefined);

  const positiveResponseSelectOptions = useSelectOptionsFromArray(
    policies?.wellbeingCheckSettings.PositiveResponse.actionOptions,
  );

  const negativeResponseWithHelpSelectOptions = useSelectOptionsFromArray(
    policies?.wellbeingCheckSettings.NegativeWithHelp.actionOptions,
  );

  const negativeResponseWithoutHelpSelectOptions = useSelectOptionsFromArray(
    policies?.wellbeingCheckSettings.NegativeNoHelp.actionOptions,
  );

  useEffect(() => {
    setWellbeingCheckSettings(policies?.wellbeingCheckSettings);
  }, [policies]);

  useEffect(() => {
    setAccessibilityPolicySettings(policies?.accessibility);
  }, [policies]);

  const saveWellbeingCheckSettings = useCallback(() => {
    if (wellbeingCheckSettings) {
      if (
        !deepEqual(policies?.wellbeingCheckSettings, wellbeingCheckSettings)
      ) {
        updateWellbeingCheckSettings(wellbeingCheckSettings);
      }
    }
  }, [
    policies?.wellbeingCheckSettings,
    wellbeingCheckSettings,
    updateWellbeingCheckSettings,
  ]);

  const saveAccessibilityPolicySettings = useCallback(() => {
    if (accessibilityPolicySettings) {
      if (!deepEqual(policies?.accessibility, accessibilityPolicySettings)) {
        updateAccessibilityPolicySettings(accessibilityPolicySettings);
      }
    }
  }, [
    policies?.accessibility,
    accessibilityPolicySettings,
    updateAccessibilityPolicySettings,
  ]);

  const handleWellBeingCheckSettingsFieldBlur = () => {
    // TODO: add smarter logic for saving
    saveWellbeingCheckSettings();
  };

  const handleAccessibilityPolicySettingsFieldBlur = () => {
    // TODO: add smarter logic for saving
    saveAccessibilityPolicySettings();
  };

  const updateWellbeingCheckSettingsProperty = useCallback(
    <TKey extends keyof WellbeingCheckSettings>(
      key: TKey,
      value: Partial<WellbeingCheckSettings[TKey]>,
    ) => {
      setWellbeingCheckSettings((state) => {
        if (!state) return state;

        const updatedWellbeingCheckSettings = { ...state };
        if (
          typeof updatedWellbeingCheckSettings[key] === 'object' &&
          typeof value === 'object'
        ) {
          updatedWellbeingCheckSettings[key] = {
            ...updatedWellbeingCheckSettings[key],
            ...value,
          };
        } else {
          // @ts-expect-error - types are equal
          updatedWellbeingCheckSettings[key] = value;
        }

        return updatedWellbeingCheckSettings;
      });
    },
    [setWellbeingCheckSettings],
  );

  const updateAccessibilityPolicySettingsProperty = useCallback(
    <TKey extends keyof AccessibilityPolicySettings>(
      key: TKey,
      value: Partial<AccessibilityPolicySettings[TKey]>,
    ) => {
      setAccessibilityPolicySettings((state) => {
        if (!state) return state;
        return { ...state, [key]: value };
      });
    },
    [setAccessibilityPolicySettings],
  );

  const isPlatformPolicyOrgPage =
    isPlatformAdmin &&
    organisation &&
    pathname === urls.policies.organisation.compose(organisation?.id);

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePrefix={
            isPlatformPolicyOrgPage
              ? organisation.name
              : strings.policies.organisation.subtitlePrefix
          }
          subtitleTooltip={strings.policies.organisation.subtitleTooltip}
          title={
            isPlatformPolicyOrgPage
              ? strings.policies.platform.organisation.title
              : strings.policies.organisation.title
          }
          tooltip={strings.policies.organisation.tooltip}
        />
      </HeadingGroupContainerLayout>
      <SectionContainerLayout horizontal className="border-0">
        <SectionHeadingGroup
          className="max-w-96"
          subtitle={strings.policies.section.addressBookSettings.description}
          title={strings.policies.section.addressBookSettings.title}
        />
        <Card className="flex flex-col gap-6">
          <AddressBookPanel></AddressBookPanel>
        </Card>
      </SectionContainerLayout>
      <SectionContainerLayout horizontal className="border-0">
        <SectionHeadingGroup
          className="max-w-96"
          subtitle={strings.policies.section.wellbeingCheckSettings.description}
          title={strings.policies.section.wellbeingCheckSettings.title}
        />
        <Card className="flex flex-col gap-6">
          <SwitchField
            description={
              strings.policies.section.wellbeingCheckSettings.fields
                .enable_automated_checks.description
            }
            enabled={wellbeingCheckSettings?.schedule ?? false}
            label={
              strings.policies.section.wellbeingCheckSettings.fields
                .enable_automated_checks.label
            }
            onBlur={handleWellBeingCheckSettingsFieldBlur}
            onChange={(enabled) => {
              updateWellbeingCheckSettingsProperty('schedule', enabled);
            }}
          />
          <Field className="flex min-h-12 flex-row items-center justify-between gap-2">
            <Label htmlFor="frequencyDays">
              {
                strings.policies.section.wellbeingCheckSettings.fields.frequency
                  .label
              }
            </Label>
            <div className="flex flex-row items-center gap-2.5 text-sm font-medium">
              <Label htmlFor="frequencyDays">Every</Label>
              <TextInput
                className="w-16"
                id="frequencyDays"
                max={14}
                min={1}
                onBlur={handleWellBeingCheckSettingsFieldBlur}
                onChange={(event) =>
                  updateWellbeingCheckSettingsProperty(
                    'frequencyDays',
                    event.target.value
                      ? Number(event.target.value)
                      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ('' as any as number),
                  )
                }
                type="number"
                value={wellbeingCheckSettings?.frequencyDays}
              />
              <label htmlFor="frequencyDays">day(s)</label>
            </div>
          </Field>
        </Card>
      </SectionContainerLayout>

      <SectionContainerLayout horizontal>
        <SectionHeadingGroup
          className="max-w-96"
          subtitle={strings.policies.section.responseAction.description}
          title={strings.policies.section.responseAction.title}
        />
        <Card className="flex flex-col">
          <Fieldset>
            <Legend
              description={
                strings.policies.section.responseAction.fieldset
                  .positive_response.description
              }
              title={
                strings.policies.section.responseAction.fieldset
                  .positive_response.title
              }
            />
            <SelectField
              hidePlaceholderOption={true}
              label="Action"
              onBlur={handleWellBeingCheckSettingsFieldBlur}
              onChange={(event) =>
                updateWellbeingCheckSettingsProperty('PositiveResponse', {
                  action: event.target.value,
                })
              }
              options={positiveResponseSelectOptions}
              value={wellbeingCheckSettings?.PositiveResponse.action}
            />
          </Fieldset>

          <CardDivider />

          <Fieldset>
            <Legend
              description={
                strings.policies.section.responseAction.fieldset
                  .negative_response_with_help.description
              }
              title={
                strings.policies.section.responseAction.fieldset
                  .negative_response_with_help.title
              }
            />
            <SelectField
              hidePlaceholderOption={true}
              label="Action"
              onBlur={handleWellBeingCheckSettingsFieldBlur}
              onChange={(event) =>
                updateWellbeingCheckSettingsProperty('NegativeWithHelp', {
                  action: event.target.value,
                })
              }
              options={negativeResponseWithHelpSelectOptions}
              value={wellbeingCheckSettings?.NegativeWithHelp.action}
            />
          </Fieldset>

          <CardDivider />

          <Fieldset>
            <Legend
              description={
                strings.policies.section.responseAction.fieldset
                  .negative_response_without_help.description
              }
              title={
                strings.policies.section.responseAction.fieldset
                  .negative_response_without_help.title
              }
            />
            <SelectField
              hidePlaceholderOption={true}
              label="Action"
              onBlur={handleWellBeingCheckSettingsFieldBlur}
              onChange={(event) =>
                updateWellbeingCheckSettingsProperty('NegativeNoHelp', {
                  action: event.target.value,
                })
              }
              options={negativeResponseWithoutHelpSelectOptions}
              value={wellbeingCheckSettings?.NegativeNoHelp.action}
            />
            <Field className="mt-6 flex min-h-12 flex-row items-center justify-between gap-2">
              <Label htmlFor="wellbeingCheckNegativeNoHelpInstancesBeforeActionTimes">
                {
                  strings.policies.section.responseAction.fieldset
                    .negative_response_without_help.fields.instancesBeforeAction
                    .label
                }
              </Label>
              <div className="flex flex-row items-center gap-2.5 text-sm font-medium">
                <TextInput
                  className="w-16"
                  id="wellbeingCheckNegativeNoHelpInstancesBeforeActionTimes"
                  max={14}
                  min={1}
                  onBlur={handleWellBeingCheckSettingsFieldBlur}
                  onChange={(event) =>
                    updateWellbeingCheckSettingsProperty('NegativeNoHelp', {
                      instancesBeforeAction: {
                        ...wellbeingCheckSettings!.NegativeNoHelp
                          .instancesBeforeAction,
                        times: event.target.value
                          ? Number(event.target.value)
                          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('' as any as number),
                      },
                    })
                  }
                  type="number"
                  value={
                    wellbeingCheckSettings?.NegativeNoHelp.instancesBeforeAction
                      .times
                  }
                />
                <Label htmlFor="wellbeingCheckNegativeNoHelpInstancesBeforeActionTimes">
                  times over
                </Label>
                <TextInput
                  className="w-16"
                  id="wellbeingCheckNegativeNoHelpInstancesBeforeActionDays"
                  max={14}
                  min={1}
                  onBlur={handleWellBeingCheckSettingsFieldBlur}
                  onChange={(event) =>
                    updateWellbeingCheckSettingsProperty('NegativeNoHelp', {
                      instancesBeforeAction: {
                        ...wellbeingCheckSettings!.NegativeNoHelp
                          .instancesBeforeAction,
                        days: event.target.value
                          ? Number(event.target.value)
                          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ('' as any as number),
                      },
                    })
                  }
                  type="number"
                  value={
                    wellbeingCheckSettings?.NegativeNoHelp.instancesBeforeAction
                      .days
                  }
                />
                <Label htmlFor="wellbeingCheckNegativeNoHelpInstancesBeforeActionDays">
                  day(s)
                </Label>
              </div>
            </Field>
          </Fieldset>

          <CardDivider className="mb-8" />

          <Fieldset>
            <Legend
              className="mb-6"
              description={
                strings.policies.section.responseAction.fieldset.no_response
                  .description
              }
              title={
                strings.policies.section.responseAction.fieldset.no_response
                  .title
              }
            />
            <Field className="flex min-h-12 flex-row items-center justify-between gap-2">
              <Label>
                {
                  strings.policies.section.responseAction.fieldset.no_response
                    .fields.waitTimeMinutes.label
                }
              </Label>
              <div className="flex flex-row items-center gap-2.5 text-sm font-medium">
                <Label htmlFor="wellbeingCheckNoResponseWaitTimeMinutes">
                  Wait
                </Label>
                <TextInput
                  className="w-16"
                  id="wellbeingCheckNoResponseWaitTimeMinutes"
                  max={1440}
                  min={5}
                  onBlur={handleWellBeingCheckSettingsFieldBlur}
                  onChange={(event) =>
                    updateWellbeingCheckSettingsProperty('NoResponse', {
                      waitTimeMinutes: event.target.value
                        ? Number(event.target.value)
                        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          ('' as any as number),
                    })
                  }
                  type="number"
                  value={wellbeingCheckSettings?.NoResponse.waitTimeMinutes}
                />
                <Label htmlFor="wellbeingCheckNoResponseWaitTimeMinutes">
                  minute(s)
                </Label>
              </div>
            </Field>
          </Fieldset>

          <CardDivider className="mb-8" />

          <Fieldset>
            <Legend
              className="mb-6"
              description="Where notifications are sent for actions"
              title="Notification default email"
            />
            <Field>
              <Label htmlFor="wellbeingCheckNotificationEmail">Email</Label>
              <TextInput
                className="w-64 invalid:bg-red-100"
                id="wellbeingCheckNotificationEmail"
                onBlur={handleWellBeingCheckSettingsFieldBlur}
                onChange={(event) => {
                  updateWellbeingCheckSettingsProperty(
                    'email',
                    event.target.value,
                  );
                }}
                type="email"
                value={wellbeingCheckSettings?.email}
              />
            </Field>
          </Fieldset>
        </Card>
      </SectionContainerLayout>

      <SectionContainerLayout horizontal className="border-0">
        <SectionHeadingGroup
          className="max-w-96"
          subtitle={strings.policies.section.accessibility.description}
          title={strings.policies.section.accessibility.title}
        />
        <Card className="flex flex-col gap-6">
          <SwitchField
            description={
              strings.policies.section.accessibility.fields.closed_captioning
                .description
            }
            enabled={accessibilityPolicySettings?.closedCaptioning ?? false}
            label={
              strings.policies.section.accessibility.fields.closed_captioning
                .label
            }
            onBlur={handleAccessibilityPolicySettingsFieldBlur}
            onChange={(enabled) => {
              updateAccessibilityPolicySettingsProperty(
                'closedCaptioning',
                enabled,
              );
            }}
          />
          <SwitchField
            description={
              strings.policies.section.accessibility.fields.magnifier
                .description
            }
            enabled={accessibilityPolicySettings?.magnifier ?? false}
            label={
              strings.policies.section.accessibility.fields.magnifier.label
            }
            onBlur={handleAccessibilityPolicySettingsFieldBlur}
            onChange={(enabled) => {
              updateAccessibilityPolicySettingsProperty('magnifier', enabled);
            }}
          />
          <Field className="flex min-h-12 flex-row items-center justify-between gap-2">
            <Label htmlFor="speakingRate">
              {
                strings.policies.section.accessibility.fields.speaking_rate
                  .label
              }
            </Label>
            <Select
              hidePlaceholderOption
              className="mt-0 w-20"
              id="speakingRate"
              onBlur={handleAccessibilityPolicySettingsFieldBlur}
              onChange={(event) =>
                updateAccessibilityPolicySettingsProperty(
                  'speakingRate',
                  event.target.value
                    ? Number(event.target.value)
                    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      ('' as any as number),
                )
              }
              options={speakingRateSelectOptions}
              value={accessibilityPolicySettings?.speakingRate}
            />
          </Field>
        </Card>
      </SectionContainerLayout>
    </>
  );
};
